import React, { useState, useMemo } from 'react';
import { FileText, Search, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';

// Les deux boutons d'un pied de modal ont la meme taille ET la meme largeur.
// DESIGN-SYSTEM.md, section Boutons.
const BOUTON_PIED = 'h-9 min-w-[168px] justify-center gap-2';

const ExportTimetableModal = ({ 
  isOpen, 
  onClose, 
  onExport, 
  classes = [], 
  teachers = [],
  activeYear
}) => {
  const [exportType, setExportType] = useState('class');
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Réinitialiser lors du changement de type
  const handleTypeChange = (newType) => {
    setExportType(newType);
    setSelectedIds([]);
    setSearchTerm('');
  };

  // Items à afficher selon le type
  const items = exportType === 'class' ? classes : teachers;

  // Filtrer selon la recherche
  const filteredItems = useMemo(() => {
    if (!searchTerm) return items;
    
    return items.filter(item => {
      if (exportType === 'class') {
        return item.nom?.toLowerCase().includes(searchTerm.toLowerCase());
      } else {
        const fullName = `${item.nom} ${item.prenom}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase());
      }
    });
  }, [items, searchTerm, exportType]);

  // Toggle sélection d'un item
  const handleToggleItem = (id) => {
    setSelectedIds(prev => 
      prev.includes(id.toString()) 
        ? prev.filter(itemId => itemId !== id.toString())
        : [...prev, id.toString()]
    );
  };

  // Tout sélectionner / désélectionner
  const handleToggleAll = () => {
    if (selectedIds.length === filteredItems.length && filteredItems.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredItems.map(item => item.id.toString()));
    }
  };

  // Gérer l'export
  const handleExport = () => {
    if (selectedIds.length === 0) return;
    onExport('pdf', selectedIds, exportType);
  };

  // Réinitialiser à la fermeture
  const handleClose = () => {
    setSelectedIds([]);
    setSearchTerm('');
    setExportType('class');
    onClose();
  };

  if (!isOpen) return null;

  const isAllSelected = filteredItems.length > 0 && selectedIds.length === filteredItems.length;

  return (
    <Dialog open={isOpen} onOpenChange={(ouvert) => { if (!ouvert) handleClose(); }}>
      <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
            <FileText size={18} />
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Exporter en PDF
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Un emploi du temps par classe ou par enseignant.
            </DialogDescription>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Type d'export */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Type d'export
            </label>
            <div className="flex gap-3">
              {/* Plus de dégradé ni de `scale-105` : un bouton qui grandit
                  déplace ce qui l'entoure. La teinte pleine de la marque
                  suffit à dire lequel est actif. */}
              <button
                onClick={() => handleTypeChange('class')}
                className={`flex-1 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors ${
                  exportType === 'class'
                    ? 'border-transparent bg-[#246990] text-white shadow-sm'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Par classes
              </button>
              <button
                onClick={() => handleTypeChange('teacher')}
                className={`flex-1 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors ${
                  exportType === 'teacher'
                    ? 'border-transparent bg-[#246990] text-white shadow-sm'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Par enseignants
              </button>
            </div>
          </div>

          {/* Sélection */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-gray-700">
                Sélection des {exportType === 'class' ? 'classes' : 'enseignants'}
              </label>
              <button
                onClick={handleToggleAll}
                className="text-sm font-semibold text-[#246990] transition-colors hover:text-[#1f5a7a]"
              >
                {isAllSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
              </button>
            </div>

            {/* Barre de recherche */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder={`Rechercher ${exportType === 'class' ? 'une classe' : 'un enseignant'}…`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9 w-full rounded-lg border border-gray-200 pl-9 pr-3 text-sm outline-none transition-colors focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
              />
            </div>

            {/* Liste avec checkboxes modernes */}
            <div className="max-h-80 overflow-y-auto rounded-lg border border-gray-200">
              {filteredItems.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  Aucun résultat trouvé
                </div>
              ) : (
                filteredItems.map((item, index) => (
                  <label
                    key={item.id}
                    className={`flex items-center px-4 py-3 cursor-pointer transition-all hover:bg-gray-50 ${
                      index !== filteredItems.length - 1 ? 'border-b border-gray-100' : ''
                    }`}
                  >
                    {/* Custom Checkbox */}
                    <div className="relative flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(item.id.toString())}
                        onChange={() => handleToggleItem(item.id)}
                        className="sr-only"
                      />
                      {/* Ni degrade ni `scale-110` : une case qui grandit
                          quand on la coche fait bouger toute la ligne. */}
                      <div className={`flex h-5 w-5 items-center justify-center rounded border transition-colors ${
                        selectedIds.includes(item.id.toString())
                          ? 'border-transparent bg-[#246990]'
                          : 'border-gray-300 hover:border-[#246990]'
                      }`}>
                        {selectedIds.includes(item.id.toString()) && (
                          <Check className="text-white" size={13} />
                        )}
                      </div>
                    </div>

                    {/* Label */}
                    <span className={`ml-3 text-sm font-medium transition-colors ${
                      selectedIds.includes(item.id.toString()) ? 'text-gray-900' : 'text-gray-700'
                    }`}>
                      {exportType === 'class' 
                        ? item.nom 
                        : `${item.nom} ${item.prenom || ''}`
                      }
                    </span>
                  </label>
                ))
              )}
            </div>

            {/* Compteur de sélection */}
            <div className="mt-3 text-sm text-gray-600">
              {selectedIds.length} élément{selectedIds.length > 1 ? 's' : ''} sélectionné{selectedIds.length > 1 ? 's' : ''}
            </div>
          </div>
        </div>

        <DialogFooter className="border-t bg-gray-50 px-5 py-4">
          <Button variant="outline" className={BOUTON_PIED} onClick={handleClose}>
            Annuler
          </Button>
          <Button
            onClick={handleExport}
            disabled={selectedIds.length === 0}
            className={`${BOUTON_PIED} border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]`}
          >
            <FileText className="h-4 w-4" />
            Exporter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportTimetableModal;